import { FILLER_TILES, ORE_TILES } from '../data/tiles'
import type { FillerTile, GridCell } from '../types/tiles'

type RNG = () => number

function mulberry32(seed: number): RNG {
  let s = seed
  return function (): number {
    s |= 0
    s = (s + 0x6D2B79F5) | 0
    let t = Math.imul(s ^ (s >>> 15), 1 | s)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0)
  }
}

function rowSeed(baseSeed: number, rowIndex: number): number {
  let h = baseSeed ^ (rowIndex * 2654435761)
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b)
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b)
  return h ^ (h >>> 16)
}

function oreThreshold(rarityBp: number, depth: number, minDepth: number, luckMult: number): number {
  const depthBonusBp = Math.min((depth - minDepth) * 10, 5000)
  const chanceBp = Math.trunc(rarityBp * (10000 + depthBonusBp) * luckMult / 1_000_000)
  return Math.trunc(chanceBp * 4294967296 / 10000)
}

export interface GridGenerator {
  generateRow(depth: number): GridCell[]
}

export function createGridGenerator(baseSeed: number, luckMult: number = 100): GridGenerator {

  function getFillerTile(depth: number, rng: RNG): FillerTile {
    const candidates = FILLER_TILES.filter(t => depth >= t.minDepth && depth <= t.maxDepth)
    if (candidates.length === 0) {
      return FILLER_TILES[FILLER_TILES.length - 1]
    }
    const idx = Math.floor(rng() * candidates.length / 4294967296)
    return candidates[idx]
  }

  function generateTile(depth: number, rng: RNG): GridCell {
    const availableOres = ORE_TILES.filter(o => depth >= o.minDepth)

    for (const ore of availableOres) {
      const threshold = oreThreshold(ore.rarityBp, depth, ore.minDepth, luckMult)
      if (rng() < threshold) {
        return { type: 'ore', tile: ore }
      }
    }

    return { type: 'filler', tile: getFillerTile(depth, rng) }
  }

  function generateRow(depth: number): GridCell[] {
    const rng = mulberry32(rowSeed(baseSeed, depth))
    const row: GridCell[] = []
    for (let col = 0; col < 5; col++) {
      row.push(generateTile(depth, rng))
    }
    return row
  }

  return { generateRow }
}
